import { EditOutlined, PlusOutlined, UndoOutlined } from '@ant-design/icons'
import { Button, InputNumber, Segmented, Select, Tag, Tooltip, Typography } from 'antd'
import { FC, useState } from 'react'
import styled from 'styled-components'
import { formatPantoneLabel } from '../../../../../../helpers/entities/pantone/format-pantone-label'
import { getBrandLetter } from '../../../../../../helpers/entities/pantone/get-brand-letter'
import Pantone from '../../../../../../types/api/pantone'
import { PaintsManufacturer } from '../../../../../../types/api/print-job-paints'
import ColorantCell, { ColorantCellState } from './colorant-cell'
import { getReference } from './helpers/get-reference'
import { isAmountMissing } from './helpers/is-amount-missing'
import { isCompositionChanged } from './helpers/is-composition-changed'
import { nextCellKey } from './helpers/next-cell-key'
import { switchBrand } from './helpers/switch-brand'
import { toCellDrafts } from './helpers/to-cell-drafts'
import { ColorantCellDraft, PaintRowDraft } from './types'

const ColumnStyled = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 12px;
    border: 1px solid ${({ theme }) => theme.colorBorderSecondary};
    border-radius: ${({ theme }) => theme.borderRadiusLG}px;
    background: ${({ theme }) => theme.colorFillQuaternary};
`

const TitleLine = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`

const PantoneTitle = styled(Typography.Text)`
    font-size: 16px;
    flex: 1;
    min-width: 0;
`

const VersionSelect = styled(Select)`
    flex: none;
    width: 72px;
`

const BrandSwitch = styled(Segmented)`
    flex: none;
    width: 64px;
`

const PortionsInput = styled(InputNumber)`
    width: 100%;
`

const CompositionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 4px;
    padding-top: 8px;
    border-top: 1px solid ${({ theme }) => theme.colorBorderSecondary};
`

const CellsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`

interface PantoneColumnProps {
    draft: PaintRowDraft
    manufacturers: PaintsManufacturer[]
    colorantOptions: { value: string; label: string }[]
    showErrors: boolean
    onChange: (draft: PaintRowDraft) => void
}

/** Карточка флексоформы: сверху пантон, версия, бренд и порции, под ним состав (редактируется после «Новая версия»). */
const PantoneColumn: FC<PantoneColumnProps> = ({
    draft,
    manufacturers,
    colorantOptions,
    showErrors,
    onChange,
}) => {
    const [editing, setEditing] = useState(false)
    const reference = getReference(draft)
    const changed = isCompositionChanged(draft)
    const brandVersions = draft.versions[draft.manufacturerDocumentId ?? ''] ?? []
    const hasAnyRecipe = Object.values(draft.versions).some(list => list.length)
    const nextVersion = Math.max(0, ...brandVersions.map(item => item.version ?? 0)) + 1
    const referenceKeys = new Set(
        (reference?.colorants ?? []).map(item => `${item.colorant?.documentId}:${item.amount}`)
    )

    const getCellState = (cell: ColorantCellDraft): ColorantCellState => {
        if (showErrors && isAmountMissing(cell)) return 'error'
        if (!referenceKeys.has(`${cell.colorantDocumentId}:${cell.amount}`)) return 'changed'
        return 'default'
    }

    const setColorants = (colorants: ColorantCellDraft[]) => onChange({ ...draft, colorants })

    const selectVersion = (pantone: Pantone) =>
        onChange({
            ...draft,
            referencePantoneDocumentId: pantone.documentId,
            colorants: toCellDrafts(pantone.colorants),
        })

    return (
        <ColumnStyled>
            <TitleLine>
                <PantoneTitle strong ellipsis>
                    {draft.pantoneName || '—'}
                </PantoneTitle>
                <Tooltip title='Версия рецепта'>
                    <VersionSelect
                        size='small'
                        placeholder='—'
                        disabled={!brandVersions.length}
                        value={reference ? draft.referencePantoneDocumentId : undefined}
                        onChange={value =>
                            selectVersion(brandVersions.find(item => item.documentId === value)!)
                        }
                        options={brandVersions.map(item => ({
                            value: item.documentId,
                            label: item.version ?? '—',
                        }))}
                    />
                </Tooltip>
                <BrandSwitch
                    size='small'
                    block
                    value={draft.manufacturerDocumentId ?? undefined}
                    onChange={value => onChange(switchBrand(draft, value as string))}
                    options={manufacturers.map(item => ({
                        value: item.documentId,
                        label: getBrandLetter(item.name),
                        title: item.name,
                        disabled: hasAnyRecipe && !draft.versions[item.documentId]?.length,
                    }))}
                />
            </TitleLine>
            <Typography.Text type='secondary'>
                Форма {draft.form.num} · {draft.form.colorName}
            </Typography.Text>
            {!reference && !draft.colorants.length ? (
                <Tag>
                    {hasAnyRecipe
                        ? 'Выберите версию или соберите состав'
                        : 'Рецепта нет — соберите состав'}
                </Tag>
            ) : changed ? (
                <Tag color='warning'>
                    Новая версия {formatPantoneLabel(draft.pantoneName, nextVersion)}
                </Tag>
            ) : (
                <Tag color='success'>
                    По рецепту {formatPantoneLabel(draft.pantoneName, reference?.version)}
                </Tag>
            )}
            <PortionsInput
                min={0}
                step={0.5}
                placeholder='Порций'
                addonAfter='порц.'
                status={
                    showErrors && !draft.portions && draft.colorants.length ? 'error' : undefined
                }
                value={draft.portions}
                onChange={portions => onChange({ ...draft, portions: portions as number | null })}
            />
            <CompositionHeader>
                <Typography.Text type='secondary'>Состав, г</Typography.Text>
                <Tooltip title={editing ? 'Закончить редактирование' : 'Редактировать состав'}>
                    <Button
                        size='small'
                        type={editing ? 'primary' : 'default'}
                        icon={<EditOutlined />}
                        onClick={() => setEditing(!editing)}
                    >
                        Новая версия
                    </Button>
                </Tooltip>
            </CompositionHeader>
            <CellsList>
                {draft.colorants.map((cell, index) => (
                    <ColorantCell
                        key={cell.key}
                        value={cell}
                        state={getCellState(cell)}
                        options={colorantOptions}
                        readOnly={!editing}
                        onChange={value =>
                            setColorants(
                                draft.colorants.map((item, i) => (i === index ? value : item))
                            )
                        }
                        onRemove={() => setColorants(draft.colorants.filter((_, i) => i !== index))}
                    />
                ))}
                {editing && (
                    <Button
                        type='dashed'
                        block
                        icon={<PlusOutlined />}
                        onClick={() => setColorants([...draft.colorants, { key: nextCellKey() }])}
                    />
                )}
                {editing && changed && reference && (
                    <Button
                        size='small'
                        icon={<UndoOutlined />}
                        onClick={() => setColorants(toCellDrafts(reference.colorants))}
                    >
                        Вернуть рецепт
                    </Button>
                )}
            </CellsList>
        </ColumnStyled>
    )
}

export default PantoneColumn
